import React, { useMemo } from 'react';
import styles from './expired-knowledge-widget.module.scss';
import { KnowledgeWrapperComponent } from '../../../knowledge/components/knowledge-row';
import { SectionComponent } from 'src/components/common/section/section.component';
import { formatFullName, formatNumber } from 'shared/utils';
import { colorPalette } from 'shared/constants';
import { Divider, notification, Progress } from 'antd';
import classNames from 'classnames';
import { PandaThumbsUp } from 'shared/images/characters';
import _ from 'lodash';
import ownersServiceApi from 'shared/api/owners.service.api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { ListItemsNavigator } from 'src/components/common/items-navigator';
import { Loader } from 'src/components/core';

const ExpiredKnowledgeWidgetComponent = React.memo(function ExpiredKnowledgeWidgetComponent({
  title = '',
  items = {},
  allowEditExpired = false,
  isLoading = false,
  hasError = false,
  userId,
  owners,
  knowledgeItemsCount,
  lang,
}) {
  const [emailSents, setEmailSents] = React.useState([]);
  const [loading, setLoading] = React.useState([]);
  const itemsForReview = useMemo(
    () =>
      new Set(
        Object.values(items)
          .flat()
          .map(item => item.id),
      ).size,
    [items],
  );
  const percentage = useMemo(
    () =>
      itemsForReview === 0 ? 100 : Math.floor((1 - itemsForReview / knowledgeItemsCount) * 100),
    [itemsForReview, knowledgeItemsCount],
  );
  const { unassigned, ...restItems } = items;
  const [api, contextHolder] = notification.useNotification();

  if (isLoading) {
    return (
      <div className={styles.content}>
        <label className={styles.title}>{title}</label>
        <div className={classNames(styles.widgetItems, styles.noContent)}>
          <Loader />
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className={styles.content}>
        <label className={styles.title}>{title}</label>
        <div className={classNames(styles.widgetItems, styles.noContent)}>
          <span>{lang.ERROR_OCCURRED}</span>
        </div>
      </div>
    );
  }

  if (_.isArray(items) && _.isEmpty(items)) {
    return null;
  }

  const getColorFromPercentage = percentage => {
    if (percentage <= 80) {
      return colorPalette.red;
    } else if (percentage <= 90) {
      return colorPalette.orange;
    } else if (percentage < 100) {
      return colorPalette.primaryMain5;
    } else {
      return colorPalette.greenPrimary;
    }
  };

  const renderTitle = (id, size) => {
    const owner = owners.find(o => o.id === id);
    const name = formatFullName(owner, {
      labelInParenthesis: owner?.isDeactivated ? lang.DEACTIVATED : null,
    });
    return `${name} (${formatNumber(size)})`;
  };

  const showError = () =>
    api.error({
      title: lang.ERROR_OCCURRED,
      placement: 'bottomRight',
    });

  const renderEmailReminder = id => {
    if (id === userId) return null;

    if (loading.includes(id)) {
      return <FontAwesomeIcon className={styles.loadingIcon} icon={faSpinner} spin />;
    }

    const emailWasSent = emailSents.includes(id);

    const onNotifyOwner = async e => {
      e.stopPropagation();
      e.preventDefault();

      setLoading(loading => [...loading, id]);
      try {
        const res = await ownersServiceApi.notifyOwner({
          ownerId: id,
          items: items[id].map(({ id }) => id),
        });

        if (res.error) {
          showError();
          return;
        }
        setEmailSents(sent => [...sent, id]);
      } catch (error) {
        console.error('Error sending email', error);
        showError();
      } finally {
        setLoading(loading => loading.filter(i => i !== id));
      }
    };

    return (
      <button
        className={classNames([styles.email, emailWasSent && styles.emailSent])}
        onClick={onNotifyOwner}
        type="button"
        disabled={emailWasSent}>
        {emailWasSent ? lang.EMAIL_SENT : lang.EMAIL_REMINDER}
      </button>
    );
  };

  return (
    <div className={styles.content}>
      {contextHolder}
      <label className={styles.title}>{title}</label>
      <div className={styles.widgetItems}>
        <div className={styles.header}>
          <h3 className={styles.count}>
            {itemsForReview > 0
              ? lang.ITEMS_UP_FOR_REVIEW?.replace('{count}', formatNumber(itemsForReview))
              : lang.ITEMS_UP_TO_DATE}
          </h3>
          <div className={styles.progress}>
            <Progress
              percent={percentage}
              showInfo={false}
              strokeColor={getColorFromPercentage(percentage)}
              className={styles.progressBar}
            />
            <p>
              <strong>{percentage}%</strong>
              {lang.PERCENTAGE_LABEL}
            </p>
          </div>
        </div>
        <Divider className={styles.titleDivider} />
        {Object.values(items).length > 0 ? (
          <ListItemsNavigator
            orientation="vertical"
            aria-label="Expired knowledge sections by owner">
            {Object.entries(restItems).map(([ownerId, itemsForReview = []], i) => (
              <SectionComponent
                title={renderTitle(ownerId, itemsForReview.length)}
                customActions={renderEmailReminder(ownerId)}
                defaultIsOpen={false}
                backgroundColor={colorPalette.white}
                key={i}>
                <ListItemsNavigator
                  orientation="vertical"
                  aria-label="Expired knowledge items"
                  className={styles.items}>
                  {itemsForReview?.map(item => (
                    <KnowledgeWrapperComponent
                      key={item.id}
                      item={item}
                      draggable={false}
                      showSpecialty
                      allowEditExpired={allowEditExpired}
                    />
                  ))}
                </ListItemsNavigator>
              </SectionComponent>
            ))}
            {unassigned && (
              <SectionComponent
                title={`Unassigned (${formatNumber(unassigned.length)})`}
                defaultIsOpen={false}
                backgroundColor={colorPalette.white}
                key="unassigned">
                <ListItemsNavigator
                  orientation="vertical"
                  aria-label="Expired knowledge items"
                  className={styles.items}>
                  {unassigned?.map(item => (
                    <KnowledgeWrapperComponent
                      key={item.id}
                      item={item}
                      draggable={false}
                      showSpecialty
                      allowEditExpired={allowEditExpired}
                    />
                  ))}
                </ListItemsNavigator>
              </SectionComponent>
            )}
          </ListItemsNavigator>
        ) : (
          <div className={styles.noContent}>
            <img src={PandaThumbsUp} alt="panda thumbs up" />
            <p>{lang.NO_CONTENT}</p>
          </div>
        )}
      </div>
    </div>
  );
});

export { ExpiredKnowledgeWidgetComponent };
